import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import customerService from '../services/customerService';
import AssignUserModal from '../components/AssignUserModal';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import DeleteConfirmationModal from '../components/common/DeleteConfirmationModal';


const CustomersListLayer = () => {
  
  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (!customerToDelete) return;
    
    setIsDeleting(true);
    try {
      // Call the delete API
      await customerService.deleteCustomer(customerToDelete.id);
      
      // Show success message
      toast.success('Customer deleted successfully');
      
      // Update the UI by removing the deleted customer from the list
      setCustomers(prevCustomers => 
        prevCustomers.filter(customer => customer.id !== customerToDelete.id)
      );
      
      // Update pagination total count
      setPagination(prev => ({
        ...prev,
        total: Math.max(0, prev.total - 1)
      }));
      
      // Check if we need to adjust the page
      const isLastItemOnPage = customers.length === 1;
      const isNotFirstPage = pagination.page > 1;
      
      if (isLastItemOnPage && isNotFirstPage) {
        // If we deleted the last item on a page, go to the previous page
        setPagination(prev => ({
          ...prev,
          page: prev.page - 1
        }));
      }
      
      // Close the modal
      setShowDeleteModal(false);
      
    } catch (error) {
      console.error('Error deleting customer:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete customer';
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
      setCustomerToDelete(null);
    }
  };
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    company_name: '',
    contact_name: '',
    status: '',
    is_active: true
  });
  
  // State for delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Assign User Modal State
  const [showAssignUserModal, setShowAssignUserModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [assigningUsers, setAssigningUsers] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [assignedUserIds, setAssignedUserIds] = useState([]);

  // Fetch users for assignment when modal opens
  useEffect(() => {
    const fetchUsers = async () => {
      if (showAssignUserModal && selectedCustomer) {
        try {
          // TODO: Replace with actual API call to fetch users
          // const users = await userService.getUsers();
          // setAvailableUsers(users);
          
          // For now, using mock data
          const availableUsers = await customerService.getAvailableUsers(selectedCustomer.id);
          setAvailableUsers(availableUsers);
          
          
          // Set initially assigned users (if any)
          if (selectedCustomer.assigned_users) {
            setAssignedUserIds(selectedCustomer.assigned_users.map(u => u.id));
          } else {
            setAssignedUserIds([]);
          }
        } catch (error) {
          console.error('Error fetching users:', error);
          toast.error('Failed to load users');
        }
      }
    };
    
    fetchUsers();
  }, [showAssignUserModal, selectedCustomer]);

  // Handle assigning a single user to customer
  const handleAssignUsers = async (userIds) => {
    // Since we're using radio buttons, we should only get one user ID
    const userId = Array.isArray(userIds) ? userIds[0] : userIds;
    
    if (!selectedCustomer || !userId) {
      console.error('Invalid selectedCustomer or userId:', { selectedCustomer, userId });
      toast.error('Please select a user to assign');
      return;
    }
    
    setAssigningUsers(true);
    console.log('Assigning user to customer:', { customerId: selectedCustomer.id, userId });
    
    try {
      // Call the API to assign the user
      await customerService.assignUser(selectedCustomer.id, userId);
      console.log(`Successfully assigned user ${userId} to customer ${selectedCustomer.id}`);
      
      // Find the assigned user from available users
      const assignedUser = availableUsers.find(u => 
        String(u.id) === String(userId) || String(u.email) === String(userId)
      );
      
      if (assignedUser) {
        // Update local state with the new assignment
        setCustomers(prevCustomers => 
          prevCustomers.map(customer => 
            customer.id === selectedCustomer.id 
              ? { 
                  ...customer, 
                  assigned_users: [assignedUser]
                } 
              : customer
          )
        );
      }
      
      toast.success('User assigned successfully');
      setShowAssignUserModal(false);
    } catch (error) {
      console.error('Error assigning user:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign user');
    } finally {
      setAssigningUsers(false);
    }
  };
  
  // Handle modal close
  const handleCloseAssignUserModal = () => {
    setShowAssignUserModal(false);
    setSelectedCustomer(null);
    setAssignedUserIds([]);
  };

  // Debug: Log user context and JWT token
  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    
    if (token) {
      try {
        // Decode the JWT token to see its contents
        const payload = JSON.parse(atob(token.split('.')[1]));
        
        // Safely format dates only if they exist and are valid
        const formatDate = (timestamp) => {
          if (!timestamp) return 'N/A';
          try {
            return new Date(timestamp * 1000).toISOString();
          } catch (e) {
            console.warn('Invalid timestamp:', timestamp, e);
            return 'Invalid date';
          }
        };
        
        console.log('[CustomersListLayer] JWT Token payload:', {
          user_id: payload.user_id || 'N/A',
          email: payload.sub || 'N/A',
          exp: formatDate(payload.exp),
          iat: formatDate(payload.iat),
          isAdmin: payload.is_admin || false
        });
      } catch (e) {
        console.error('[CustomersListLayer] Error decoding JWT token:', e);
      }
    }
  }, [user]);

  // Calculate owner ID based on user role
  const ownerId = React.useMemo(() => {
    return (user?.role !== 'ADMIN' && user?.role !== 'SUPERUSER') ? user?.id : null;
  }, [user?.id, user?.role]);

  // Prepare filters for the API
  const preparedFilters = React.useMemo(() => ({
    ...filters,
    status: filters.status || undefined,
    is_active: (filters.is_active !== '') ? filters.is_active : undefined
  }), [filters]);

  // Fetch customers function
  const fetchCustomers = React.useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const { page, limit } = pagination;
      const response = await customerService.getCustomers({
        page,
        limit,
        ownerId,
        filters: preparedFilters
      });
      
      setCustomers(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0
      }));
      setError(null);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to load customers. Please try again.');
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [user?.id, pagination, ownerId, preparedFilters]);

  // Fetch customers when component mounts or when fetchCustomers changes
  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  };

  // Handle limit change
  const handleLimitChange = (e) => {
    setPagination(prev => ({
      ...prev,
      limit: Number(e.target.value),
      page: 1 // Reset to first page
    }));
  };

  // Handle search input
  const handleSearch = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    // Reset to first page when searching
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  };

  // Handle status filter change
  const handleStatusChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({
      ...prev,
      status: value === 'All' ? '' : value
    }));
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Calculate pagination info
  const startItem = (pagination.page - 1) * pagination.limit + 1;
  const endItem = Math.min(startItem + customers.length - 1, pagination.total);

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">Show</span>
          <select 
            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px" 
            value={pagination.limit}
            onChange={handleLimitChange}
            disabled={loading}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
          
          <div className="navbar-search">
            <input
              type="text"
              className="bg-base h-40-px w-auto"
              name="company_name"
              placeholder="Search by company"
              value={filters.company_name || ''}
              onChange={handleSearch}
              disabled={loading}
            />
            <Icon icon="ion:search-outline" className="icon" />
          </div>
          
          <div className="navbar-search">
            <input
              type="text"
              className="bg-base h-40-px w-auto"
              name="contact_name"
              placeholder="Search by contact"
              value={filters.contact_name || ''}
              onChange={handleSearch}
              disabled={loading}
            />
            <Icon icon="ion:search-outline" className="icon" />
          </div>
          
          <select 
            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px" 
            value={filters.status || ''}
            onChange={handleStatusChange}
            disabled={loading}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        {(user?.role === 'ADMIN' || user?.role === 'SUPERUSER') && (

          <Link
            to="/admin/customers/create"
            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
            size="sm"
          >
            <Icon
              icon="ic:baseline-plus"
              className="icon text-xl line-height-1"
            />
            Add New Customer
          </Link>
        )}
      </div>

      {/* Assign User Modal */}
      <AssignUserModal
        show={showAssignUserModal}
        onHide={() => setShowAssignUserModal(false)}
        customerId={selectedCustomer?.id}
        onSuccess={() => {
          // Refresh the customer list after successful assignment
          fetchCustomers();
        }}
      />

      <div className="card-body p-24">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 mb-0">Loading customers...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted">No customers found</p>
          </div>
        ) : (
          <>
            <div className="table-responsive scroll-sm">
              <table className="table bordered-table sm-table mb-0">
                <thead>
                  <tr>
                    <th scope="col">
                      <div className="d-flex align-items-center gap-10">
                        <div className="form-check style-check d-flex align-items-center">
                          <input className="form-check-input radius-4 border input-form-dark" type="checkbox" name="checkbox" id="selectAll"/>
                        </div>
                        ID
                      </div>
                    </th>
                    <th scope="col">COMPANY NAME</th>
                    <th scope="col">CONTACT PERSON</th>
                    <th scope="col">EMAIL</th>
                    <th scope="col">PHONE</th>
                    <th scope="col">STATUS</th>
                    <th scope="col">CREATED DATE</th>
                    <th scope="col">Total Licenses</th>
                    <th className="text-end">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer, index) => {
                    const rowNumber = (pagination.page - 1) * pagination.limit + index + 1;
                    
                    return (
                      <tr key={customer.id}>
                        <td>
                          <div className="d-flex align-items-center gap-10">
                            <div className="form-check style-check d-flex align-items-center">
                              <input className="form-check-input radius-4 border border-neutral-400" type="checkbox" name="checkbox"/>
                            </div>
                            {rowNumber}
                            
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {customer.company_logo && (() => {
                              // Use the same URL handling logic as in CustomerProfileLayer
                              let logoUrl = customer.company_logo;
                              if (logoUrl.startsWith('uploads/')) {
                                // Convert uploads/filename.jpg to full backend URL
                                const filename = logoUrl.replace('uploads/', '');
                                const backendUrl = process.env.REACT_APP_ROOT_URL || 'http://localhost:8000';
                                logoUrl = `${backendUrl.replace('/api/v1', '')}/static/${filename}`;
                              } else if (logoUrl.startsWith('/static/')) {
                                // Ensure static URLs are absolute
                                const backendUrl = process.env.REACT_APP_ROOT_URL || 'http://localhost:8000';
                                logoUrl = `${backendUrl.replace('/api/v1', '')}${logoUrl}`;
                              }
                              
                              return (
                                <img 
                                  src={logoUrl}
                                  alt={customer.company_name}
                                  className="rounded-circle"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    objectFit: 'cover',
                                    border: customer.status === 'active' ? '1px solid #19f705' : '1px solid #fc0202',
                                    boxSizing: 'border-box'
                                  }}
                                />
                              );
                            })()}
                            <span>{customer.company_name}</span>
                          </div>
                        </td>
                        <td>{customer.contact_name}</td>
                        <td>{customer.company_email}</td>
                        <td>{customer.company_phone || '-'}</td>
                        <td>
                          <span className={customer.status === 'active' ? 'bg-success-focus text-success-600 border border-success-main px-24 py-4 radius-4 fw-medium text-sm' : 'bg-secondary-focus text-secondary-600 border border-secondary-main px-24 py-4 radius-4 fw-medium text-sm'}>
                            {customer.status?.charAt(0).toUpperCase() + customer.status?.slice(1) || 'N/A'}
                          </span>
                        </td>
                        <td>{formatDate(customer.created_at)}</td>
                        <td>{customer.total_licenses}</td>
                        <td className="text-end">
                        <div className="d-flex align-items-center gap-10 justify-content-center">
                          <Link
                              to={`/admin/customers/${customer.id}`}
                              className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              title="View Customer">
                              <Icon icon="majesticons:eye-line" className="icon text-xl"/>
                          </Link>
                          {user.role !== 'USER' && (
                            <Link
                                to={`/admin/customers/${customer.id}`}
                                className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                title="Edit Customer"
                                disabled={user.role === 'USER'}
                                >
                                <Icon icon="lucide:edit" className="menu-icon" />
                            </Link>
                          )}
                          {user.role !== 'USER' && (
                            <button
                                type="button"
                                className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                onClick={() => {
                                  setCustomerToDelete(customer);
                                  setShowDeleteModal(true);
                                }}>
                                <Icon icon="fluent:delete-24-regular" className="menu-icon"/>
                            </button>
                          )}
                          {user.role !== 'USER' && (
                            <button
                                type="button"
                                className="bg-warning-focus bg-hover-warning-200 text-warning-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                title="Assign User"
                                onClick={() => {
                                  setSelectedCustomer(customer);
                                  setShowAssignUserModal(true);
                                }}>
                                <Icon icon="mdi:user-plus" className="icon text-lg" />
                            </button>
                          )}
                          

                      </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="text-muted">
                Showing {startItem} to {endItem} of {pagination.total} entries
              </div>
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1 || loading}
                    >
                      Previous
                    </button>
                  </li>
                  {Array.from({ length: Math.ceil(pagination.total / pagination.limit) }, (_, i) => i + 1)
                    .slice(0, 5)
                    .map(pageNum => (
                      <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                        <button 
                          className="page-link"
                          onClick={() => handlePageChange(pageNum)}
                          disabled={loading}
                        >
                          {pageNum}
                        </button>
                      </li>
                    ))
                  }
                  <li className={`page-item ${pagination.page * pagination.limit >= pagination.total ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page * pagination.limit >= pagination.total || loading}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </>
        )}
      </div>

      {/* Reusable Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
        itemName={customerToDelete?.company_name}
        itemType="customer"
      />
      
      {/* Assign User Modal */}
      <AssignUserModal
        open={showAssignUserModal}
        onClose={handleCloseAssignUserModal}
        title={`Assign Users to ${selectedCustomer?.company_name || 'Customer'}`}
        users={availableUsers}
        assignedUsers={assignedUserIds}
        selectedCustomerId={selectedCustomer?.id}
        onAssign={handleAssignUsers}
        loading={assigningUsers}
      />
    </div>
  );
};

export default CustomersListLayer;
